import React, { useEffect, useState, useRef } from 'react';
import ItemDetailDrawer from '../components/ItemDetailDrawer';
import usePersistedState from '../usePersistedState';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

const CLASSIFICATIONS = ['RAW MATERIALS', 'INGREDIENTS', 'FINISHED GOODS', 'CONSUMABLES'];

export default function ItemMaster() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistedState('items.search', '');
  const [catFilter, setCatFilter] = useState('');
  const [clsFilter, setClsFilter] = usePersistedState('items.cls', '');
  const [showAdd, setShowAdd] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ item_code: '', item_name: '', unit: 'Kg', unit_cost: '', category: '', classification: '', rm_subtype: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    API.get('/items').then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
    API.get('/items/categories').then(r => setCategories(r.data.categories || []));
  };
  useEffect(() => { load(); }, []);

  const location = useLocation();
  useEffect(() => {
    const s = location.state?.itemSearch;
    if (s) { setSearch(s); window.history.replaceState({}, ''); }
    // eslint-disable-next-line
  }, [location.state]);

  const filtered = items.filter(i => {
    if (clsFilter && i.classification !== clsFilter) return false;
    if (catFilter && i.category !== catFilter) return false;
    if (search) {
      const h = ((i.item_code || '') + ' ' + (i.item_name || '')).toLowerCase();
      if (!search.toLowerCase().split(/\s+/).every(w => h.includes(w))) return false;
    }
    return true;
  });

  const startEdit = (item) => {
    setEditId(item.id);
    setForm({ item_code: item.item_code, item_name: item.item_name, unit: item.unit || 'Kg', unit_cost: item.unit_cost || '', category: item.category || '', classification: item.classification || '', rm_subtype: item.rm_subtype || '' });
  };

  const save = async () => {
    if (!form.item_code || !form.item_name) return toast.error('Code and name required');
    try {
      if (editId) {
        await API.put(`/items/${editId}`, form);
        toast.success('Item updated');
      } else {
        await API.post('/items', form);
        toast.success('Item added');
      }
      setEditId(null); setShowAdd(false);
      setForm({ item_code: '', item_name: '', unit: 'Kg', unit_cost: '', category: '', classification: '', rm_subtype: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); }
  };

  const del = async (id) => {
    if (!window.confirm('Deactivate this item?')) return;
    try { await API.delete(`/items/${id}`); toast.success('Item removed'); load(); }
    catch { toast.error('Failed'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await API.post('/items/upload-excel', fd);
      const { inserted, skipped } = r.data;
      toast.success(`${inserted} new item(s) added${skipped ? `, ${skipped} existing skipped` : ''}`);
      load();
    } catch (err) {
      const msg = err.response?.data?.error || 'Upload failed';
      const details = err.response?.data?.details;
      toast.error(msg + (details ? `\n${details.slice(0,3).join('\n')}` : ''), { duration: 6000 });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clsColor = { 'RAW MATERIALS': { bg: '#e8f0fe', c: '#1a56db' }, 'INGREDIENTS': { bg: '#fff3e0', c: '#e67e00' }, 'FINISHED GOODS': { bg: '#f0faf0', c: '#2e7d32' }, 'CONSUMABLES': { bg: '#f5f5f5', c: '#666' } };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Item Master</div>
          <div className="page-subtitle">{items.length} active items · raw materials, ingredients, finished goods & consumables</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleUpload} />
          <button className="btn btn-secondary" disabled={uploading} onClick={() => fileInputRef.current?.click()}>{uploading ? 'Uploading...' : '⬆ Upload Excel'}</button>
          <button className="btn btn-primary" onClick={() => { setShowAdd(true); setEditId(null); setForm({ item_code: '', item_name: '', unit: 'Kg', unit_cost: '', category: '', classification: '', rm_subtype: '' }); }}>+ Add Item</button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6" style={{ padding: '12px 16px' }}>
        <input placeholder="Search code or name..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 10 }} />
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Classification</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={() => setClsFilter('')} className={'btn btn-sm ' + (clsFilter === '' ? 'btn-primary' : 'btn-secondary')} style={{ fontSize: 11 }}>All</button>
            {CLASSIFICATIONS.map(c => (
              <button key={c} onClick={() => setClsFilter(c)} className={'btn btn-sm ' + (clsFilter === c ? 'btn-primary' : 'btn-secondary')} style={{ fontSize: 11 }}>{c}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Category</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={() => setCatFilter('')} className={'btn btn-sm ' + (catFilter === '' ? 'btn-primary' : 'btn-secondary')} style={{ fontSize: 11 }}>All</button>
            {categories.map(c => (
              <button key={c} onClick={() => setCatFilter(c)} className={'btn btn-sm ' + (catFilter === c ? 'btn-primary' : 'btn-secondary')} style={{ fontSize: 11 }}>{c}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ padding: '8px 4px', fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} items</div>
        {loading ? <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading...</div> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Unit</th><th className="text-right">Cost</th><th>Category</th><th>Classification</th><th>Subtype</th><th style={{ textAlign: 'center' }}>Actions</th></tr></thead>
              <tbody>
                {filtered.map(i => (
                  <tr key={i.id}>
                    <td className="mono" style={{ color: 'var(--accent)', fontSize: 11, fontWeight: 600 }}>{i.item_code}</td>
                    <td style={{ fontSize: 13 }}>{i.item_name}</td>
                    <td className="text-muted" style={{ fontSize: 12 }}>{i.unit}</td>
                    <td className="text-right mono" style={{ fontSize: 12 }}>{i.unit_cost ? 'AED ' + i.unit_cost : '—'}</td>
                    <td style={{ fontSize: 12 }}>{i.category || '—'}</td>
                    <td>{i.classification ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: (clsColor[i.classification] || {}).bg || '#f5f5f5', color: (clsColor[i.classification] || {}).c || '#666' }}>{i.classification}</span> : '—'}</td>
                    <td>{i.rm_subtype ? <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: i.rm_subtype === 'PLAIN' ? '#e8f5e9' : '#fff3e0', color: i.rm_subtype === 'PLAIN' ? '#2e7d32' : '#e65100' }}>{i.rm_subtype}</span> : '—'}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, marginRight: 4 }} onClick={() => setDetailItem(i)}>👅🥗</button>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, marginRight: 4 }} onClick={() => startEdit(i)}>Edit</button>
                      <button className="btn btn-danger btn-sm" style={{ fontSize: 10 }} onClick={() => del(i.id)}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showAdd || editId) && (
        <div className="modal-overlay" onClick={() => { setShowAdd(false); setEditId(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <div className="modal-title">{editId ? 'Edit Item' : 'Add Item'}</div>
              <button className="modal-close" onClick={() => { setShowAdd(false); setEditId(null); }}>×</button>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label>Item Code *</label>
              <input value={form.item_code} onChange={e => setForm(f => ({ ...f, item_code: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label>Item Name *</label>
              <input value={form.item_name} onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div className="form-group" style={{ margin: 0, flex: 1 }}>
                <label>Unit</label>
                <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                  {['Kg', 'g', 'L', 'ml', 'pcs', 'nos'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0, flex: 1 }}>
                <label>Unit Cost (AED)</label>
                <input type="number" step="0.01" value={form.unit_cost} onChange={e => setForm(f => ({ ...f, unit_cost: e.target.value }))} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label>Category</label>
              <input list="cat-list" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. NUTS, INGREDIENT, PM" />
              <datalist id="cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Classification</label>
              <select value={form.classification} onChange={e => setForm(f => ({ ...f, classification: e.target.value }))}>
                <option value="">— select —</option>
                {CLASSIFICATIONS.map(c => <option key={c}>{c}</option>)}
              </select>
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>RM Subtype (for raw materials)</label>
                <select value={form.rm_subtype} onChange={e => setForm(f => ({ ...f, rm_subtype: e.target.value }))}>
                  <option value="">—</option>
                  <option value="PLAIN">PLAIN</option>
                  <option value="VALUE ADDED">VALUE ADDED</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowAdd(false); setEditId(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editId ? 'Save' : 'Add'}</button>
            </div>
          </div>
        </div>
      )}
      <ItemDetailDrawer item={detailItem} onClose={() => setDetailItem(null)} />
    </div>
  );
}
